import React from "react";
import { Button, ButtonProps } from "@/components/fun-ui/Button";
import { LucideIcon } from 'lucide-react';
import { IconType } from "@/components/fun-ui/icons";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useState } from "react";

interface IconButtonProps extends ButtonProps {
  label?: string;
  Icon?: LucideIcon | IconType | any;
  clickedDelayDuration?: number;
  onTipOpenChange?: (open: boolean) => void;
  keepTipClicked?: boolean;
  asChild?: boolean;
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({
    label = '',
    Icon,
    size = 'default',
    onTipOpenChange,
    children,
    onMouseOver,
    onMouseOut,
    keepTipClicked = false,
    className = '',
    asChild = false,
    side = 'top',
    sideOffset = 4,
    ...props
  }, ref) => {
    const [open, setOpen] = useState(false);
    const [clicking, setClicking] = useState(false);

    return (
      <TooltipProvider delayDuration={100}>
        <Tooltip open={open} onOpenChange={(open) => {
          if (keepTipClicked) {
            setOpen(open || clicking);
            onTipOpenChange && onTipOpenChange(open || clicking);
          } else {
            setOpen(open);
          }
        }}>
          <TooltipTrigger asChild>
            {
              asChild ? children : (
                <Button
                  ref={ref}
                  variant="ghost"
                  size="icon"
                  className={["h-6 w-6 cursor-pointer", className].filter(Boolean).join(' ')}
                  onMouseOver={(...args) => {
                    onMouseOver && onMouseOver(...args);
                    setClicking(true);
                  }}
                  onMouseOut={(...args) => {
                    onMouseOut && onMouseOut(...args);
                    setClicking(false);
                  }}
                  {...props}
                >
                  {Icon ? <Icon size={16} /> : children}
                </Button>
              )
            }
          </TooltipTrigger>
          {
            label ? (
              <TooltipContent
                side={side}
                sideOffset={sideOffset}
                className="bg-foreground z-[9999]"
              >
                {label}
              </TooltipContent>
            ) : null
          }
        </Tooltip>
      </TooltipProvider>
    );
  }
);

IconButton.displayName = "IconButton";